/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Real Estate reference pages, rendered by the generic entity framework
 * (components/EntityFramework) through ReEntityList / ReEntityDetail.
 *
 * Keyed by the routes.js route key of the LIST page (the detail page looks its
 * config up by the same key). Field names mirror backend/app/real_estate/
 * models.py; list endpoints return {items,total,page,page_size} and read
 * models carry `<thing>_name` convenience fields (docs/real-estate/
 * api-contract.md). The hand-built screens (dashboard, inventory grid,
 * bookings, leases, ...) live in ./screens and are not configured here.
 *
 * `permissionObject` is the object api_name whose grant governs the page;
 * usePermissionCoverage.test.js checks each one against the object registry.
 * Lenders and amenities have no registered object, so they carry none and
 * fall back to the legacy (support-user) rule.
 */

import { Icon } from "@mui/material";
import { UnitMediaSection } from "layouts/RealEstate/components/MediaGallery";
import {
  EntityStatusChip,
  formatBool,
  formatDate,
  formatDateTime,
  formatEnumLabel,
  formatMoney,
  formatNumber,
  formatPct,
} from "components/EntityFramework";
import colors from "styles/tokens/colors";
import { RE_STATUS_VARIANTS } from "./reStatus";
import { defaultCurrency } from "./variant";
import ReUnitActions from "./ReUnitActions";

// ------------------------------------------------------------------ vocab
export const PROJECT_STATUSES = ["PLANNED", "LAUNCHED", "UNDER_CONSTRUCTION", "COMPLETED", "HANDED_OVER", "ON_HOLD"];
export const UNIT_STATUSES = ["AVAILABLE", "HELD", "RESERVED", "BOOKED", "SOLD", "HANDED_OVER", "LEASED", "BLOCKED"];
export const UNIT_CATEGORIES = ["APARTMENT", "VILLA", "TOWNHOUSE", "PENTHOUSE", "DUPLEX", "PLOT", "OFFICE", "RETAIL", "WAREHOUSE"];
export const VIEWING_STATUSES = ["SCHEDULED", "CONFIRMED", "COMPLETED", "NO_SHOW", "CANCELLED"];
export const VIEWING_TYPES = ["SITE_VISIT", "SHOW_FLAT", "UNIT", "VIRTUAL"];
export const INTEREST_LEVELS = ["HOT", "WARM", "COLD"];
export const LISTING_PURPOSES = ["SALE", "RENT"];
export const LISTING_STATUSES = ["DRAFT", "ACTIVE", "UNDER_OFFER", "CLOSED", "EXPIRED", "WITHDRAWN"];
export const COMMISSION_BASES = ["PERCENT_OF_NET_PRICE", "FIXED_AMOUNT", "PERCENT_OF_ANNUAL_RENT"];
export const COMMISSION_PAYEE_TYPES = ["BROKER_FIRM", "BROKER_AGENT", "INTERNAL_AGENT", "CO_BROKER"];
export const RENT_FREQUENCIES = ["MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"];
// Free-text columns in the schema; these are the values the UI offers.
export const BROKER_REGISTRATION_STATUSES = ["PENDING", "APPROVED", "SUSPENDED", "EXPIRED"];
export const LENDER_TYPES = ["BANK", "ISLAMIC_BANK", "FINANCE_COMPANY"];
export const FURNISHING = ["UNFURNISHED", "SEMI_FURNISHED", "FURNISHED"];
export const EMIRATES = ["DUBAI", "ABU_DHABI", "SHARJAH", "AJMAN", "RAS_AL_KHAIMAH", "FUJAIRAH", "UMM_AL_QUWAIN"];
export const PORTALS = ["BAYUT", "PROPERTY_FINDER", "DUBIZZLE"];

// ---------------------------------------------------------------- helpers
// The fallback currency is the tenant's (variant.js), read when a cell renders.
const money = (value, data) => formatMoney("currency", defaultCurrency())(value, data);

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const col = (Header, accessor, extra = {}) => ({ Header, accessor, ...extra });

const moneyCol = (Header, accessor) =>
  col(Header, accessor, {
    align: "right",
    Cell: ({ value, row }) => (
      <span style={{ fontFamily: "monospace" }}>{money(value, row.original)}</span>
    ),
  });

const dateCol = (Header, accessor) => col(Header, accessor, { Cell: ({ value }) => formatDate(value) });
const enumCol = (Header, accessor) => col(Header, accessor, { Cell: ({ value }) => formatEnumLabel(value) });
const boolCol = (Header, accessor) => col(Header, accessor, { Cell: ({ value }) => formatBool(value) });
const pctCol = (Header, accessor) => col(Header, accessor, { align: "right", Cell: ({ value }) => formatPct(value) });
const statusCol = (Header = "Status", accessor = "status") =>
  col(Header, accessor, {
    Cell: ({ value }) => <EntityStatusChip status={value} variants={RE_STATUS_VARIANTS} size="xs" />,
  });

/** A column whose value links to another record's page. */
const linkCol = (navigate, Header, accessor, pathOf) =>
  col(Header, accessor, {
    Cell: ({ value, row }) => {
      const path = pathOf(row.original);
      if (!path || value == null || value === "") return dash(value);
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate(path)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate(path);
          }}
          style={{ color: colors.brand.primary, fontWeight: 500, cursor: "pointer" }}
        >
          {value}
        </span>
      );
    },
  });

/** Display field spec that links to a record (EntityDetail `linkTo`). */
const linkField = (label, field, pathOf, extra = {}) => ({ label, field, linkTo: pathOf, ...extra });

const ref = (name, label, apiPath, labelField, extra = {}) => ({
  name,
  label,
  type: "reference",
  reference: { apiPath, ...(extra.reference || {}) },
  labelField,
  ...extra,
});

const contactLabel = (c) =>
  c?.full_name ||
  c?.name ||
  [c?.first_name, c?.last_name].filter(Boolean).join(" ") ||
  c?.email ||
  (c?.id ? `Contact #${c.id}` : "");

export const projectRef = (extra = {}) => ref("project_id", "Project", "/re/projects", "project_name", extra);
export const developerRef = (extra = {}) => ref("developer_id", "Developer", "/re/developers", "developer_name", extra);
export const buildingRef = (extra = {}) =>
  ref("building_id", "Building", "/re/buildings", "building_name", {
    reference: { params: (values) => (values.project_id ? { project_id: values.project_id } : {}) },
    ...extra,
  });
export const unitTypeRef = (extra = {}) =>
  ref("unit_type_id", "Unit type", "/re/unit-types", "unit_type_name", {
    reference: { params: (values) => (values.project_id ? { project_id: values.project_id } : {}) },
    ...extra,
  });
export const unitRef = (extra = {}) =>
  ref("unit_id", "Unit", "/re/units", "unit_number", {
    reference: {
      params: (values) => (values.project_id ? { project_id: values.project_id } : {}),
      getLabel: (u) => [u.unit_number, u.project_name].filter(Boolean).join(" · "),
    },
    ...extra,
  });
export const brokerFirmRef = (name = "broker_firm_id", extra = {}) =>
  ref(name, "Broker firm", "/re/broker-firms", name === "firm_id" ? "firm_name" : "broker_firm_name", extra);
export const brokerAgentRef = (extra = {}) =>
  ref("broker_agent_id", "Broker agent", "/re/broker-agents", "broker_agent_name", extra);
export const contactRef = (name, label, labelField, extra = {}) =>
  ref(name, label, "/contacts/", labelField, { reference: { getLabel: contactLabel }, ...extra });

const firmNameOf = (r) => r?.firm_name || r?.broker_firm_name;

// --------------------------------------------------------------- field sets
const developerFields = [
  { name: "name", label: "Name", required: true },
  { name: "trade_license_no", label: "Trade licence no." },
  { name: "rera_developer_no", label: "RERA developer no." },
  { name: "emirate", label: "Emirate", type: "select", options: EMIRATES },
  { name: "website", label: "Website" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email", type: "email" },
  { name: "is_self", label: "This is our own company", type: "boolean" },
  { name: "description", label: "Description", type: "textarea" },
];

const projectCreateFields = [
  { name: "name", label: "Project name", required: true },
  { name: "code", label: "Code" },
  developerRef(),
  { name: "status", label: "Status", type: "select", options: PROJECT_STATUSES, defaultValue: "PLANNED" },
  { name: "community", label: "Community" },
  { name: "sub_community", label: "Sub-community" },
  { name: "city", label: "City", defaultValue: "Dubai" },
  { name: "emirate", label: "Emirate", type: "select", options: EMIRATES },
  { name: "is_off_plan", label: "Off-plan", type: "boolean", defaultValue: true },
  { name: "launch_date", label: "Launch date", type: "date" },
  { name: "expected_handover_date", label: "Expected handover", type: "date" },
  { name: "rera_project_no", label: "RERA project no." },
  { name: "dld_project_no", label: "DLD project no." },
  { name: "escrow_bank", label: "Escrow bank" },
  { name: "escrow_account_no", label: "Escrow account no." },
  { name: "currency", label: "Currency", defaultValue: "AED" },
  { name: "dld_fee_pct", label: "DLD fee", type: "pct", defaultValue: 4 },
  // Australia (hidden for UAE tenants; reEntityVariant.js)
  { name: "state", label: "State" },
  { name: "postcode", label: "Postcode" },
  { name: "council", label: "Council" },
  { name: "strata_plan_no", label: "Strata plan no." },
  { name: "description", label: "Description", type: "textarea" },
];

const buildingFields = [
  projectRef({ required: true }),
  { name: "name", label: "Name", required: true },
  { name: "code", label: "Code" },
  { name: "floors", label: "Floors", type: "number" },
  { name: "basement_levels", label: "Basement levels", type: "number" },
  { name: "status", label: "Status", type: "select", options: PROJECT_STATUSES },
  { name: "expected_handover_date", label: "Expected handover", type: "date" },
  { name: "construction_progress_pct", label: "Construction progress", type: "pct" },
  { name: "description", label: "Description", type: "textarea" },
];

const unitTypeFields = [
  projectRef({ required: true }),
  { name: "name", label: "Name", required: true, helperText: "e.g. 2BR Type B" },
  { name: "category", label: "Category", type: "select", options: UNIT_CATEGORIES, required: true, defaultValue: "APARTMENT" },
  { name: "bedrooms", label: "Bedrooms", type: "number", helperText: "0 = studio" },
  { name: "bathrooms", label: "Bathrooms", type: "number" },
  { name: "bua_sqft", label: "BUA (sq ft)", type: "number" },
  { name: "plot_sqft", label: "Plot (sq ft)", type: "number" },
  { name: "starting_price", label: "Starting price (AED)", type: "money" },
  { name: "floor_plan_url", label: "Floor plan URL" },
  { name: "description", label: "Description", type: "textarea" },
];

const unitFields = [
  projectRef({ required: true }),
  buildingRef(),
  unitTypeRef(),
  { name: "unit_number", label: "Unit number", required: true },
  { name: "floor", label: "Floor", type: "number" },
  { name: "category", label: "Category", type: "select", options: UNIT_CATEGORIES, required: true, defaultValue: "APARTMENT" },
  { name: "bedrooms", label: "Bedrooms", type: "number" },
  { name: "bathrooms", label: "Bathrooms", type: "number" },
  { name: "bua_sqft", label: "BUA (sq ft)", type: "number" },
  { name: "plot_sqft", label: "Plot (sq ft)", type: "number" },
  { name: "balcony_sqft", label: "Balcony (sq ft)", type: "number" },
  { name: "view", label: "View" },
  { name: "facing", label: "Facing" },
  { name: "parking_spaces", label: "Parking spaces", type: "number" },
  { name: "furnishing", label: "Furnishing", type: "select", options: FURNISHING },
  { name: "list_price", label: "List price (AED)", type: "money" },
  { name: "price_per_sqft", label: "Price per sq ft", type: "money" },
  { name: "annual_rent", label: "Annual rent (AED)", type: "money" },
  { name: "service_charge_per_sqft", label: "Service charge / sq ft", type: "money" },
  { name: "title_deed_no", label: "Title deed no." },
  { name: "dewa_premise_no", label: "DEWA premise no." },
  { name: "strata_levy_quarterly", label: "Strata levy (quarterly)", type: "money" },
  { name: "council_rates_annual", label: "Council rates (annual)", type: "money" },
  { name: "land_size_sqm", label: "Land size (sq m)", type: "number" },
  { name: "notes", label: "Notes", type: "textarea" },
];

const priceListFields = [
  projectRef({ required: true }),
  { name: "name", label: "Name", required: true },
  { name: "effective_from", label: "Effective from", type: "date" },
  { name: "effective_to", label: "Effective to", type: "date" },
  { name: "is_active", label: "Active", type: "boolean", defaultValue: true },
  { name: "currency", label: "Currency", defaultValue: "AED" },
  { name: "notes", label: "Notes", type: "textarea" },
];

const brokerFirmFields = [
  { name: "name", label: "Firm name", required: true },
  { name: "rera_orn", label: "RERA ORN" },
  { name: "trade_license_no", label: "Trade licence no." },
  { name: "trade_license_expiry", label: "Trade licence expiry", type: "date" },
  { name: "emirate", label: "Emirate", type: "select", options: EMIRATES },
  { name: "registration_status", label: "Registration", type: "select", options: BROKER_REGISTRATION_STATUSES, required: true, defaultValue: "PENDING" },
  { name: "trn", label: "TRN (VAT)" },
  { name: "is_vat_registered", label: "VAT registered", type: "boolean", defaultValue: true },
  { name: "abn", label: "ABN" },
  { name: "acn", label: "ACN" },
  { name: "state_licence_no", label: "Agency licence no." },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email", type: "email" },
  { name: "bank_name", label: "Bank" },
  { name: "iban", label: "IBAN" },
  { name: "default_commission_pct", label: "Default commission", type: "pct" },
  { name: "is_internal", label: "Our own brokerage", type: "boolean" },
  { name: "notes", label: "Notes", type: "textarea" },
];

const brokerAgentFields = [
  brokerFirmRef("firm_id", { required: true }),
  { name: "name", label: "Name", required: true },
  { name: "brn", label: "BRN" },
  { name: "brn_expiry", label: "BRN expiry", type: "date" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email", type: "email" },
  { name: "commission_split_pct", label: "Share of firm commission", type: "pct" },
  { name: "is_active", label: "Active", type: "boolean", defaultValue: true },
];

const listingFields = [
  { name: "title", label: "Title", required: true, fullWidth: true },
  { name: "reference", label: "Reference" },
  { name: "purpose", label: "Purpose", type: "select", options: LISTING_PURPOSES, required: true, defaultValue: "SALE" },
  { name: "status", label: "Status", type: "select", options: LISTING_STATUSES, required: true, defaultValue: "DRAFT" },
  projectRef(),
  unitRef(),
  brokerAgentRef(),
  contactRef("landlord_contact_id", "Owner / landlord", "landlord_contact_name"),
  { name: "price", label: "Price (AED)", type: "money" },
  {
    name: "rent_frequency",
    label: "Rent frequency",
    type: "select",
    options: RENT_FREQUENCIES,
    visibleWhen: (v) => v.purpose === "RENT",
  },
  { name: "currency", label: "Currency", defaultValue: "AED" },
  { name: "trakheesi_permit_no", label: "Trakheesi permit no." },
  { name: "form_a_no", label: "Form A no." },
  { name: "form_a_expiry", label: "Form A expiry", type: "date" },
  { name: "listed_date", label: "Listed date", type: "date" },
  { name: "portals", label: "Portals", type: "list", helperText: `Comma separated: ${PORTALS.join(", ")}` },
  { name: "sale_method", label: "Sale method", type: "select", options: ["PRIVATE_TREATY", "AUCTION", "EOI"] },
  { name: "auction_at", label: "Auction date", type: "datetime", visibleWhen: (v) => v.sale_method === "AUCTION" },
  { name: "agency_agreement_type", label: "Agency agreement", type: "select", options: ["EXCLUSIVE", "SOLE", "OPEN", "AUCTION"] },
  { name: "agent_licence_no", label: "Agent licence no." },
  { name: "description", label: "Description", type: "textarea" },
];

const commissionRuleFields = [
  { name: "name", label: "Rule name", required: true },
  { name: "applies_to", label: "Applies to", type: "select", options: ["SALE", "LEASE"], required: true, defaultValue: "SALE" },
  { name: "payee_type", label: "Payee", type: "select", options: COMMISSION_PAYEE_TYPES, required: true, defaultValue: "BROKER_FIRM" },
  { name: "basis", label: "Basis", type: "select", options: COMMISSION_BASES, required: true, defaultValue: "PERCENT_OF_NET_PRICE" },
  { name: "rate_pct", label: "Rate", type: "pct", visibleWhen: (v) => v.basis !== "FIXED_AMOUNT" },
  { name: "fixed_amount", label: "Fixed amount (AED)", type: "money", visibleWhen: (v) => v.basis === "FIXED_AMOUNT" },
  projectRef({ helperText: "Empty = every project" }),
  unitTypeRef({ helperText: "Most specific rule wins" }),
  brokerFirmRef("broker_firm_id", { helperText: "Empty = every firm" }),
  { name: "agent_split_pct", label: "Agent split", type: "pct" },
  { name: "co_broker_split_pct", label: "Co-broker split", type: "pct" },
  { name: "eligible_after_paid_pct", label: "Payable once buyer has paid", type: "pct", defaultValue: 0 },
  { name: "apply_vat", label: "Add VAT", type: "boolean", defaultValue: true },
  { name: "valid_from", label: "Valid from", type: "date" },
  { name: "valid_to", label: "Valid to", type: "date" },
  { name: "priority", label: "Priority", type: "number", defaultValue: 0 },
  { name: "is_active", label: "Active", type: "boolean", defaultValue: true },
];

const lenderFields = [
  { name: "name", label: "Lender", required: true },
  { name: "lender_type", label: "Type", type: "select", options: LENDER_TYPES },
  { name: "contact_name", label: "Contact name" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email", type: "email" },
  { name: "max_ltv_resident_pct", label: "Max LTV (resident)", type: "pct" },
  { name: "max_ltv_non_resident_pct", label: "Max LTV (non-resident)", type: "pct" },
  { name: "referral_fee_pct", label: "Referral fee", type: "pct" },
  { name: "is_active", label: "Active", type: "boolean", defaultValue: true },
  { name: "notes", label: "Notes", type: "textarea" },
];

const amenityFields = [
  { name: "name", label: "Name", required: true },
  { name: "category", label: "Category" },
  { name: "icon", label: "Icon", helperText: "Material icon name, e.g. pool" },
];

const viewingEditFields = [
  { name: "status", label: "Status", type: "select", options: VIEWING_STATUSES, required: true },
  { name: "scheduled_at", label: "Scheduled at", type: "datetime", required: true },
  { name: "duration_minutes", label: "Duration (minutes)", type: "number" },
  { name: "viewing_type", label: "Type", type: "select", options: VIEWING_TYPES },
  { name: "location", label: "Location" },
  { name: "attended_at", label: "Attended at", type: "datetime" },
  { name: "interest_level", label: "Interest", type: "select", options: INTEREST_LEVELS },
  { name: "next_step", label: "Next step" },
  { name: "feedback", label: "Feedback", type: "textarea" },
];

// Display specs for detail pages.
const display = (fields) =>
  fields.map((f) => {
    const out = { label: f.label, field: f.labelField || f.name };
    if (f.type === "money") out.renderValue = money;
    else if (f.type === "pct") out.renderValue = formatPct;
    else if (f.type === "date") out.renderValue = formatDate;
    else if (f.type === "datetime") out.renderValue = formatDateTime;
    else if (f.type === "boolean") out.renderValue = formatBool;
    else if (f.type === "select") out.renderValue = formatEnumLabel;
    else if (f.type === "list") out.renderValue = (v) => (Array.isArray(v) && v.length ? v.map(formatEnumLabel).join(", ") : "-");
    else if (f.type === "number") out.renderValue = (v) => formatNumber(v, 2);
    if (f.type === "textarea") out.fullWidth = true;
    if (f.name === "status" || f.name === "registration_status") out.type = "status";
    return out;
  });

const projectLink = (r) => (r?.project_id ? `/real-estate/projects/${r.project_id}` : null);
const unitLink = (r) => (r?.unit_id ? `/real-estate/units/${r.unit_id}` : null);
const firmLink = (r) => {
  const firmId = r?.firm_id ?? r?.broker_firm_id;
  return firmId ? `/real-estate/broker-firms/${firmId}` : null;
};

const withLinks = (fields, links) =>
  display(fields).map((f) => (links[f.field] ? { ...f, linkTo: links[f.field] } : f));

// ------------------------------------------------------------------- configs
export const RE_ENTITY_CONFIG = {
  "re-developers": {
    permissionObject: "re_developers",
    title: "Developer",
    subtitle: "Master and project developers.",
    addLabel: "Add Developer",
    apiPath: "/re/developers",
    listApiPath: "/re/developers",
    detailApiPath: "/re/developers",
    route: "/real-estate/developers",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    createForm: { fields: developerFields },
    editFields: developerFields,
    detailFields: display(developerFields),
    columns: () => [
      col("Name", "name"),
      enumCol("Emirate", "emirate"),
      col("RERA no.", "rera_developer_no"),
      col("Phone", "phone"),
      col("Email", "email"),
      boolCol("Own company", "is_self"),
    ],
  },

  "re-projects": {
    permissionObject: "re_projects",
    title: "Project",
    subtitle: "Developments, their status and handover dates.",
    addLabel: "New Project",
    apiPath: "/re/projects",
    listApiPath: "/re/projects",
    detailApiPath: "/re/projects",
    // The project page is a hand-built screen (screens/ProjectDetail).
    route: "/real-estate/projects",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    openCreated: true,
    filters: [{ param: "status", label: "Status", options: PROJECT_STATUSES }],
    urlParams: ["developer_id"],
    createForm: { fields: projectCreateFields },
    columns: () => [
      col("Project", "name"),
      col("Code", "code"),
      col("Developer", "developer_name"),
      col("Community", "community"),
      statusCol(),
      dateCol("Handover", "expected_handover_date"),
      pctCol("Built", "construction_progress_pct"),
      // Counted from the project's units; total_units is an optional typed-in plan figure.
      col("Units", "unit_count", {
        align: "right",
        disableSortBy: true,
        Cell: ({ value, row }) => value ?? row.original.total_units ?? "-",
      }),
    ],
  },

  "re-buildings": {
    permissionObject: "re_buildings",
    title: "Building",
    subtitle: "Towers and clusters within a project.",
    addLabel: "Add Building",
    apiPath: "/re/buildings",
    listApiPath: "/re/buildings",
    detailApiPath: "/re/buildings",
    route: "/real-estate/buildings",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    urlParams: ["project_id"],
    createForm: { fields: buildingFields },
    editFields: buildingFields,
    detailFields: withLinks(buildingFields, { project_name: projectLink }),
    columns: (navigate) => [
      col("Building", "name"),
      linkCol(navigate, "Project", "project_name", projectLink),
      col("Code", "code"),
      col("Floors", "floors", { align: "right" }),
      statusCol(),
      dateCol("Handover", "expected_handover_date"),
      pctCol("Built", "construction_progress_pct"),
    ],
  },

  "re-unit-types": {
    permissionObject: "re_unit_types",
    title: "Unit Type",
    subtitle: "Floor plans: bedrooms, areas and starting prices.",
    addLabel: "Add Unit Type",
    apiPath: "/re/unit-types",
    listApiPath: "/re/unit-types",
    detailApiPath: "/re/unit-types",
    route: "/real-estate/unit-types",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    urlParams: ["project_id"],
    createForm: { fields: unitTypeFields },
    editFields: unitTypeFields,
    detailFields: withLinks(unitTypeFields, { project_name: projectLink }),
    columns: (navigate) => [
      col("Unit type", "name"),
      linkCol(navigate, "Project", "project_name", projectLink),
      enumCol("Category", "category"),
      col("Beds", "bedrooms", { align: "right", Cell: ({ value }) => (value === 0 ? "Studio" : dash(value)) }),
      col("BUA (sq ft)", "bua_sqft", { align: "right", Cell: ({ value }) => formatNumber(value) }),
      moneyCol("From", "starting_price"),
    ],
  },

  "re-units": {
    permissionObject: "re_units",
    title: "Unit",
    subtitle: "Every sellable or leasable unit and its status.",
    addLabel: "Add Unit",
    apiPath: "/re/units",
    listApiPath: "/re/units",
    detailApiPath: "/re/units",
    route: "/real-estate/units",
    pagination: "server",
    primaryField: "unit_number",
    supportsDelete: true,
    searchPlaceholder: "Search unit number...",
    statusVariants: RE_STATUS_VARIANTS,
    filters: [
      { param: "status", label: "Status", options: UNIT_STATUSES },
      { param: "category", label: "Category", options: UNIT_CATEGORIES },
    ],
    urlParams: ["project_id", "building_id", "unit_type_id", "bedrooms"],
    createForm: { fields: unitFields },
    editFields: unitFields,
    // Status is changed by holds, bookings and leases, or the Block action;
    // it is shown, not edited.
    detailSections: [
      {
        title: "Unit",
        fields: [
          { label: "Status", field: "status", type: "status" },
          { label: "Status since", field: "status_changed_at", renderValue: formatDateTime },
          { label: "Hold expires", field: "hold_expires_at", renderValue: formatDateTime },
          ...withLinks(unitFields.slice(0, 3), { project_name: projectLink }),
          ...display(unitFields.slice(3, 15)),
        ],
      },
      { title: "Pricing", fields: display(unitFields.slice(15, 19)) },
      { title: "Registration", fields: display(unitFields.slice(19)) },
    ],
    // Hold / Book / Block, by the unit's current status.
    headerActions: (record, ctx) => <ReUnitActions unit={record} {...ctx} />,
    renderExtra: (record) => (record?.id ? <UnitMediaSection unitId={record.id} /> : null),
    rowActions: (row, { navigate }) => {
      const actions = [];
      if (row.status === "AVAILABLE" || row.status === "HELD") {
        actions.push({
          label: "Book",
          icon: <Icon fontSize="small">real_estate_agent</Icon>,
          onClick: () => navigate(`/real-estate/bookings/new?unit_id=${row.id}`),
        });
      }
      return actions;
    },
    columns: (navigate) => [
      col("Unit", "unit_number"),
      linkCol(navigate, "Project", "project_name", projectLink),
      col("Building", "building_name"),
      col("Type", "unit_type_name"),
      col("Floor", "floor", { align: "right" }),
      col("Beds", "bedrooms", { align: "right", Cell: ({ value }) => (value === 0 ? "Studio" : dash(value)) }),
      col("BUA (sq ft)", "bua_sqft", { align: "right", Cell: ({ value }) => formatNumber(value) }),
      moneyCol("List price", "list_price"),
      statusCol(),
      col("", "book", {
        disableSortBy: true,
        align: "center",
        Cell: ({ row }) =>
          row.original.status === "AVAILABLE" || row.original.status === "HELD" ? (
            <button
              type="button"
              onClick={() => navigate(`/real-estate/bookings/new?unit_id=${row.original.id}`)}
              style={{
                border: "1px solid #1565C8",
                color: "#1565C8",
                background: "transparent",
                borderRadius: 999,
                padding: "2px 10px",
                fontSize: "0.7rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Book
            </button>
          ) : null,
      }),
    ],
  },

  "re-amenities": {
    title: "Amenity",
    subtitle: "Reference list attached to projects.",
    addLabel: "Add Amenity",
    apiPath: "/re/amenities",
    listApiPath: "/re/amenities",
    // Small reference list, read in one request and paged in the browser.
    pagination: "client",
    baseParams: { page_size: 200 },
    objectType: "re-amenity",
    primaryField: null,
    supportsDelete: false,
    createForm: { fields: amenityFields },
    columns: () => [
      col("Amenity", "name", {
        Cell: ({ value, row }) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {row.original.icon ? <Icon fontSize="small">{row.original.icon}</Icon> : null}
            {dash(value)}
          </span>
        ),
      }),
      col("Category", "category"),
    ],
  },

  "re-price-lists": {
    permissionObject: "re_price_lists",
    title: "Price List",
    subtitle: "Unit prices by project; apply one to update list prices.",
    addLabel: "New Price List",
    apiPath: "/re/price-lists",
    listApiPath: "/re/price-lists",
    detailApiPath: "/re/price-lists",
    route: "/real-estate/price-lists",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    openCreated: true,
    urlParams: ["project_id"],
    createForm: { fields: priceListFields },
    editFields: priceListFields,
    detailFields: withLinks(priceListFields, { project_name: projectLink }),
    relatedRecords: [
      {
        key: "items",
        title: "Unit prices",
        emptyMessage: "No unit prices yet.",
        getItems: (record) => (record?.items || []).map((it) => ({ ...it, id: it.unit_id })),
        primaryField: "unit_number",
        getSecondary: (item) => money(item.price, { currency: defaultCurrency() }),
        getDetailPath: (id) => `/real-estate/units/${id}`,
        showStatus: false,
        showUpdated: false,
        allowAdd: true,
        addForm: {
          title: "Set unit price",
          fields: [
            unitRef({ required: true }),
            { name: "price", label: "Price (AED)", type: "money", required: true },
          ],
          defaults: (record) => ({ project_id: record?.project_id }),
          // Items are replaced as a whole list: merge the new price in.
          onSubmit: (payload, record, axios) => {
            const others = (record?.items || [])
              .filter((it) => String(it.unit_id) !== String(payload.unit_id))
              .map((it) => ({ unit_id: it.unit_id, price: it.price }));
            return axios.put(`/re/price-lists/${record.id}/items`, [
              ...others,
              { unit_id: payload.unit_id, price: payload.price },
            ]);
          },
        },
      },
    ],
    columns: (navigate) => [
      col("Price list", "name"),
      linkCol(navigate, "Project", "project_name", projectLink),
      dateCol("From", "effective_from"),
      dateCol("To", "effective_to"),
      boolCol("Active", "is_active"),
      col("Units", "item_count", { align: "right" }),
    ],
  },

  "re-cancellations": {
    permissionObject: "re_cancellations",
    title: "Cancellation",
    subtitle: "Cancelled bookings with forfeit and refund amounts.",
    listApiPath: "/re/cancellations",
    pagination: "server",
    primaryField: "booking_number",
    detailRoute: (row) => (row.booking_id ? `/real-estate/bookings/${row.booking_id}` : null),
    urlParams: ["project_id"],
    supportsDelete: false,
    searchable: false,
    columns: (navigate) => [
      col("Booking", "booking_number"),
      linkCol(navigate, "Project", "project_name", projectLink),
      linkCol(navigate, "Unit", "unit_number", unitLink),
      col("Reason", "reason"),
      dateCol("Effective", "effective_date"),
      moneyCol("Paid", "amount_paid"),
      pctCol("Forfeit %", "forfeit_pct"),
      moneyCol("Forfeited", "forfeit_amount"),
      moneyCol("Refund", "refund_amount"),
      col("Refund status", "refund_status", {
        Cell: ({ value }) => <EntityStatusChip status={value} variants={RE_STATUS_VARIANTS} size="xs" />,
      }),
    ],
  },

  "re-broker-firms": {
    permissionObject: "re_broker_firms",
    title: "Broker Firm",
    subtitle: "Brokerage agencies and channel partners.",
    addLabel: "Add Broker Firm",
    apiPath: "/re/broker-firms",
    listApiPath: "/re/broker-firms",
    detailApiPath: "/re/broker-firms",
    route: "/real-estate/broker-firms",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    statusVariants: RE_STATUS_VARIANTS,
    filters: [{ param: "registration_status", label: "Registration", options: BROKER_REGISTRATION_STATUSES }],
    createForm: { fields: brokerFirmFields },
    editFields: brokerFirmFields,
    detailSections: [
      { title: "Firm", fields: display(brokerFirmFields) },
      {
        title: "Performance",
        fields: [
          ["Bookings", (st) => dash(st?.bookings)],
          ["Sales value", (st) => money(st?.sales_value, { currency: defaultCurrency() })],
          ["Commission accrued", (st) => money(st?.commission_accrued, { currency: defaultCurrency() })],
          ["Commission paid", (st) => money(st?.commission_paid, { currency: defaultCurrency() })],
        ].map(([label, value], i) => ({
          label,
          field: `stats_${i}`,
          custom: (data) => (
            <span style={{ fontSize: "0.875rem", color: colors.text.light }}>
              <strong>{label}:</strong> {value(data?.stats)}
            </span>
          ),
        })),
      },
    ],
    relatedRecords: [
      {
        key: "agents",
        title: "Agents",
        emptyMessage: "No agents registered for this firm.",
        getItems: (record) => record?.agents || [],
        primaryField: "name",
        getSecondary: (item) => [item.brn ? `BRN ${item.brn}` : null, item.phone].filter(Boolean).join(" · ") || null,
        getDetailPath: (id) => `/real-estate/broker-agents/${id}`,
        showStatus: false,
        showUpdated: false,
        allowAdd: true,
        addForm: {
          title: "Add agent",
          apiPath: "/re/broker-agents",
          fields: brokerAgentFields.filter((f) => f.name !== "firm_id"),
          defaults: (record) => ({ firm_id: record?.id }),
        },
      },
    ],
    columns: () => [
      col("Firm", "name"),
      col("RERA ORN", "rera_orn"),
      enumCol("Emirate", "emirate"),
      statusCol("Registration", "registration_status"),
      pctCol("Default %", "default_commission_pct"),
      col("Agents", "agent_count", { align: "right" }),
      boolCol("Internal", "is_internal"),
    ],
  },

  "re-broker-agents": {
    permissionObject: "re_broker_agents",
    title: "Broker Agent",
    subtitle: "Registered agents of each broker firm.",
    addLabel: "Add Agent",
    apiPath: "/re/broker-agents",
    listApiPath: "/re/broker-agents",
    detailApiPath: "/re/broker-agents",
    route: "/real-estate/broker-agents",
    pagination: "server",
    primaryField: "name",
    supportsDelete: true,
    urlParams: ["firm_id"],
    createForm: { fields: brokerAgentFields },
    editFields: brokerAgentFields,
    detailFields: display(brokerAgentFields).map((f) =>
      f.field === "firm_name" ? { ...f, linkTo: firmLink, renderValue: (_v, data) => dash(firmNameOf(data)) } : f
    ),
    columns: (navigate) => [
      col("Agent", "name"),
      linkCol(navigate, "Firm", "firm_name", firmLink),
      col("BRN", "brn"),
      dateCol("BRN expiry", "brn_expiry"),
      col("Phone", "phone"),
      col("Email", "email"),
      boolCol("Active", "is_active"),
    ],
  },

  "re-listings": {
    permissionObject: "re_listings",
    title: "Listing",
    subtitle: "Units marketed for sale or rent.",
    addLabel: "New Listing",
    apiPath: "/re/listings",
    listApiPath: "/re/listings",
    detailApiPath: "/re/listings",
    route: "/real-estate/listings",
    pagination: "server",
    primaryField: "title",
    supportsDelete: true,
    statusVariants: RE_STATUS_VARIANTS,
    filters: [
      { param: "purpose", label: "Purpose", options: LISTING_PURPOSES },
      { param: "status", label: "Status", options: LISTING_STATUSES },
    ],
    urlParams: ["project_id"],
    createForm: { fields: listingFields },
    editFields: listingFields,
    detailFields: withLinks(listingFields, { project_name: projectLink, unit_number: unitLink }),
    columns: (navigate) => [
      col("Listing", "title"),
      col("Ref", "reference"),
      enumCol("Purpose", "purpose"),
      linkCol(navigate, "Unit", "unit_number", unitLink),
      col("Project", "project_name"),
      moneyCol("Price", "price"),
      statusCol(),
      dateCol("Listed", "listed_date"),
      col("Agent", "assigned_to_name"),
    ],
  },

  "re-commission-rules": {
    permissionObject: "re_commission_rules",
    title: "Commission Rule",
    subtitle: "Most specific active rule wins: unit type, then project, then firm, then default.",
    addLabel: "New Rule",
    apiPath: "/re/commission-rules",
    listApiPath: "/re/commission-rules",
    pagination: "server",
    primaryField: null,
    supportsDelete: true,
    filters: [
      { param: "applies_to", label: "Applies to", options: ["SALE", "LEASE"] },
      { param: "is_active", label: "Active", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
    ],
    urlParams: ["project_id", "broker_firm_id"],
    createForm: { fields: commissionRuleFields },
    editForm: { fields: commissionRuleFields },
    columns: () => [
      col("Rule", "name"),
      enumCol("Applies to", "applies_to"),
      enumCol("Payee", "payee_type"),
      enumCol("Basis", "basis"),
      col("Rate / amount", "rate_pct", {
        align: "right",
        Cell: ({ row }) =>
          row.original.basis === "FIXED_AMOUNT"
            ? money(row.original.fixed_amount, { currency: defaultCurrency() })
            : formatPct(row.original.rate_pct),
      }),
      col("Scope", "project_name", {
        Cell: ({ row }) =>
          [row.original.project_name, row.original.unit_type_name, row.original.broker_firm_name]
            .filter(Boolean)
            .join(" · ") || "Default",
      }),
      pctCol("Eligible after", "eligible_after_paid_pct"),
      col("Priority", "priority", { align: "right" }),
      boolCol("Active", "is_active"),
    ],
  },

  "re-lenders": {
    title: "Lender",
    subtitle: "Banks and finance companies for mortgage applications.",
    addLabel: "Add Lender",
    apiPath: "/re/lenders",
    listApiPath: "/re/lenders",
    pagination: "server",
    primaryField: null,
    supportsDelete: true,
    filters: [
      { param: "is_active", label: "Active", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
    ],
    createForm: { fields: lenderFields },
    editForm: { fields: lenderFields },
    columns: () => [
      col("Lender", "name"),
      enumCol("Type", "lender_type"),
      col("Contact", "contact_name"),
      col("Phone", "phone"),
      pctCol("Max LTV (res.)", "max_ltv_resident_pct"),
      pctCol("Max LTV (non-res.)", "max_ltv_non_resident_pct"),
      pctCol("Referral fee", "referral_fee_pct"),
      boolCol("Active", "is_active"),
    ],
  },

  // Viewings are listed by the calendar screen; this is the detail page only.
  "re-viewing-detail": {
    permissionObject: "re_viewings",
    title: "Viewing",
    detailApiPath: "/re/viewings",
    route: "/real-estate/viewings",
    primaryField: "scheduled_at",
    getTitle: (r) =>
      [r.project_name, r.unit_number].filter(Boolean).join(" · ") ||
      (r.scheduled_at ? `Viewing ${formatDateTime(r.scheduled_at)}` : null),
    supportsDelete: true,
    statusVariants: RE_STATUS_VARIANTS,
    editFields: viewingEditFields,
    detailSections: [
      {
        title: "Viewing",
        fields: [
          { label: "Status", field: "status", type: "status" },
          { label: "Scheduled", field: "scheduled_at", renderValue: formatDateTime },
          { label: "Duration", field: "duration_minutes", renderValue: (v) => (v ? `${v} min` : "-") },
          { label: "Type", field: "viewing_type", renderValue: formatEnumLabel },
          { label: "Location", field: "location" },
          { label: "Assigned to", field: "assigned_to_name" },
        ],
      },
      {
        title: "Who and what",
        fields: [
          linkField("Lead", "lead_name", (r) => (r?.lead_id ? `/leads/${r.lead_id}` : null)),
          linkField("Contact", "contact_name", (r) => (r?.contact_id ? `/contacts/${r.contact_id}` : null)),
          linkField("Project", "project_name", projectLink),
          linkField("Unit", "unit_number", unitLink),
          linkField("Listing", "listing_title", (r) => (r?.listing_id ? `/real-estate/listings/${r.listing_id}` : null)),
          linkField("Broker firm", "broker_firm_name", (r) => (r?.broker_firm_id ? `/real-estate/broker-firms/${r.broker_firm_id}` : null)),
          { label: "Broker agent", field: "broker_agent_name" },
        ],
      },
      {
        title: "Outcome",
        fields: [
          { label: "Attended", field: "attended_at", renderValue: formatDateTime },
          { label: "Interest", field: "interest_level", renderValue: formatEnumLabel },
          { label: "Next step", field: "next_step" },
          { label: "Feedback", field: "feedback", fullWidth: true },
        ],
      },
    ],
    columns: () => [],
  },
};

export const getReEntityConfig = (key) => RE_ENTITY_CONFIG[key] || null;
